/**
 * Scheduler abstraction for timer operations.
 * Enables deterministic testing by allowing timers to be controlled in tests.
 */

/**
 * Interface for scheduler operations.
 */
export interface Scheduler {
    /**
     * Schedule a callback to run after a delay.
     * @param callback - The function to call after the delay
     * @param delay - The delay in milliseconds
     * @returns A timer ID that can be used to cancel the scheduled callback
     */
    setTimeout(callback: () => void, delay: number): number

    /**
     * Cancel a scheduled callback.
     * @param id - The timer ID returned by setTimeout
     */
    clearTimeout(id: number): void

    /**
     * Schedule a callback to run repeatedly at a fixed interval.
     * @param callback - The function to call at each interval
     * @param delay - The interval in milliseconds
     * @returns An interval ID that can be used to cancel the interval
     */
    setInterval(callback: () => void, delay: number): number

    /**
     * Cancel a scheduled interval.
     * @param id - The interval ID returned by setInterval
     */
    clearInterval(id: number): void

    /**
     * Schedule a callback to run before the next repaint.
     * @param callback - The function to call before the next repaint
     * @returns A frame ID that can be used to cancel the scheduled callback
     */
    requestAnimationFrame(callback: () => void): number

    /**
     * Cancel a scheduled animation frame.
     * @param id - The frame ID returned by requestAnimationFrame
     */
    cancelAnimationFrame(id: number): void
}

/**
 * Browser scheduler implementation using window.setTimeout/clearTimeout.
 * Use this in production code.
 */
export const browserScheduler: Scheduler = {
    setTimeout: (callback, delay) => window.setTimeout(callback, delay),
    clearTimeout: (id) => window.clearTimeout(id),
    setInterval: (callback, delay) => window.setInterval(callback, delay),
    clearInterval: (id) => window.clearInterval(id),
    requestAnimationFrame: (callback) => window.requestAnimationFrame(callback),
    cancelAnimationFrame: (id) => window.cancelAnimationFrame(id),
}

/**
 * Timer entry for test scheduler tracking.
 */
interface TimerEntry {
    callback: () => void
    delay: number
    isInterval: boolean
    scheduledAt: number
}

/**
 * Factory function to create a test scheduler with controllable timers.
 * Use this in tests to control timer behavior.
 *
 * @returns A Scheduler instance with additional test control methods
 */
export const createTestScheduler = (): Scheduler & {
    /**
     * Immediately execute all pending callbacks and clear them.
     * For intervals, executes once and keeps the interval scheduled.
     */
    flush: () => void
    /**
     * Return the number of pending timers (timeouts + intervals).
     */
    pending: () => number
    /**
     * Get all pending timers (for debugging).
     */
    getTimers: () => Array<{ id: number; delay: number; isInterval: boolean }>
    /**
     * Advance virtual time by the specified milliseconds.
     * Executes any timers that would fire within that time window.
     * @param ms - The number of milliseconds to advance
     */
    advanceTime: (ms: number) => void
    /**
     * Get the current virtual time.
     */
    getCurrentTime: () => number
} => {
    const timers = new Map<number, TimerEntry>()
    let nextId = 1
    let currentTime = 0

    const executeTimersUpTo = (targetTime: number) => {
        // Keep executing until no more timers should fire
        let hasExecuted = true
        while (hasExecuted) {
            hasExecuted = false
            const sortedTimers = Array.from(timers.entries())
                .filter(([, t]) => t.scheduledAt + t.delay <= targetTime)
                .sort((a, b) => a[1].scheduledAt + a[1].delay - (b[1].scheduledAt + b[1].delay))

            for (const [id, timer] of sortedTimers) {
                if (!timers.has(id)) continue // Timer may have been cleared

                if (timer.isInterval) {
                    // For intervals, update scheduledAt and keep running
                    timer.scheduledAt = timer.scheduledAt + timer.delay
                    timer.callback()
                } else {
                    // For timeouts, remove and execute
                    timers.delete(id)
                    timer.callback()
                }
                hasExecuted = true
            }
        }
    }

    return {
        setTimeout: (callback, delay) => {
            const id = nextId++
            timers.set(id, { callback, delay, isInterval: false, scheduledAt: currentTime })
            return id
        },
        clearTimeout: (id) => {
            timers.delete(id)
        },
        setInterval: (callback, delay) => {
            const id = nextId++
            timers.set(id, { callback, delay, isInterval: true, scheduledAt: currentTime })
            return id
        },
        clearInterval: (id) => {
            timers.delete(id)
        },
        requestAnimationFrame: (callback) => {
            // For test scheduler, treat RAF like setTimeout with 0 delay
            const id = nextId++
            timers.set(id, { callback, delay: 0, isInterval: false, scheduledAt: currentTime })
            return id
        },
        cancelAnimationFrame: (id) => {
            timers.delete(id)
        },
        flush: () => {
            // Execute all pending callbacks (timeouts once, intervals once)
            const entries = Array.from(timers.entries())
            for (const [id, timer] of entries) {
                timer.callback()
                if (!timer.isInterval) {
                    timers.delete(id)
                }
            }
        },
        pending: () => timers.size,
        getTimers: () =>
            Array.from(timers.entries()).map(([id, t]) => ({
                id,
                delay: t.delay,
                isInterval: t.isInterval,
            })),
        advanceTime: (ms: number) => {
            const targetTime = currentTime + ms
            executeTimersUpTo(targetTime)
            currentTime = targetTime
        },
        getCurrentTime: () => currentTime,
    }
}
